import React from 'react';
import {
    CHART_LIBRARY_TABLE_CLASS,
    DATA_TABLE_CHART_REPORT_CLASS,
    REPORT_TABLE_CELL_CLASS,
    REPORT_TABLE_CELL_FONT_STYLE,
    REPORT_TABLE_FONT_CLASS,
    REPORT_TABLE_TEXT_CLASS,
    REPORT_TABLE_HEADER_TEXT_CLASS,
    REPORT_TABLE_WRAPPER_CLASS,
} from '../../utils/reportTableTokens';
import { Badge } from '../ui/Badge';
import ChartFrame from './ChartFrame';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableHeadCell,
    TableCell,
    TableHeaderRow,
    TableEmpty,
} from '../ui/Table';

export interface TableColumnDef {
    key: string;
    label?: string;
    align?: 'left' | 'center' | 'right';
    /** `primary` = bold label column; `strong` = bold metric (e.g. share %) */
    emphasis?: 'primary' | 'strong';
    type?: 'text' | 'status';
}

export interface DataTableChartData {
    columns?: TableColumnDef[];
    rows?: Record<string, unknown>[];
}

export interface DataTableChartOptions {
    variant?: 'default' | 'compact' | 'spacious' | 'report';
    striped?: boolean;
    maxRows?: number;
    emptyMessage?: string;
}

export interface DataTableChartProps {
    data?: DataTableChartData;
    options?: DataTableChartOptions;
    className?: string;
    /** Title rendered inside the shared ChartFrame border (data from catalog). */
    title?: string;
    /** Type tag shown top-right (e.g. TABLE). */
    badge?: string;
}

const STATUS_VARIANT_MAP: Record<string, string> = {
    'Active': 'success',
    'Inactive': 'error',
    'Pending': 'warning',
};

const DEFAULT_COLUMNS: TableColumnDef[] = [
    { key: 'restaurant', label: 'Restaurant', emphasis: 'primary' },
    { key: 'status', label: 'Status', align: 'center', type: 'status' },
    { key: 'orders', label: 'Orders', align: 'right' },
    { key: 'revenue_k', label: 'Revenue (K)', align: 'right' },
    { key: 'avg_items', label: 'Avg Items', align: 'right' },
    { key: 'share_pct', label: 'Share %', align: 'right', emphasis: 'strong' },
];

const DEFAULT_ROWS: Record<string, unknown>[] = [
    { restaurant: 'Hydrabadi Dum Biryani Butwal', status: 'Active', orders: 385, revenue_k: '34349.0', avg_items: '1.5', share_pct: '21.6%' },
    { restaurant: 'KFC Chitwan', status: 'Active', orders: 186, revenue_k: '18240.5', avg_items: '2.3', share_pct: '15.4%' },
    { restaurant: 'Pizza Hut Kathmandu', status: 'Pending', orders: 172, revenue_k: '16890.0', avg_items: '2.1', share_pct: '12.8%' },
    { restaurant: 'Burger House Pokhara', status: 'Active', orders: 158, revenue_k: '14220.0', avg_items: '1.9', share_pct: '11.2%' },
    { restaurant: 'Momo Corner Lalitpur', status: 'Inactive', orders: 143, revenue_k: '12850.5', avg_items: '1.7', share_pct: '9.8%' },
    { restaurant: 'Sushi Express Bhaktapur', status: 'Active', orders: 121, revenue_k: '11420.0', avg_items: '2.0', share_pct: '8.4%' },
    { restaurant: 'Thakali Kitchen Dharan', status: 'Pending', orders: 98, revenue_k: '9120.0', avg_items: '1.6', share_pct: '6.9%' },
    { restaurant: 'Cafe Sunrise Biratnagar', status: 'Active', orders: 87, revenue_k: '7840.5', avg_items: '1.4', share_pct: '5.5%' },
    { restaurant: 'Tandoor Express Hetauda', status: 'Inactive', orders: 76, revenue_k: '6920.0', avg_items: '1.8', share_pct: '4.8%' },
    { restaurant: 'Noodle Bar Itahari', status: 'Active', orders: 64, revenue_k: '5480.0', avg_items: '1.5', share_pct: '3.6%' },
];

const formatCell = (value: unknown): string => {
    if (value == null || value === '') return '—';
    return String(value);
};

const resolveCellClass = (
    col: TableColumnDef,
    colIndex: number,
    variant: DataTableChartOptions['variant'],
): string => {
    if (variant !== 'report') return '';

    const emphasis =
        col.emphasis ||
        (colIndex === 0 ? 'primary' : col.key.toLowerCase().includes('share') ? 'strong' : undefined);

    if (emphasis === 'primary') {
        return REPORT_TABLE_CELL_CLASS.primary;
    }
    if (emphasis === 'strong') {
        return REPORT_TABLE_CELL_CLASS.strong;
    }
    return REPORT_TABLE_CELL_CLASS.numeric;
};

/**
 * Adaptive column widths for report tables. The first (label) column gets ~1.8x
 * the weight of the rest so it has room for names, and every other column shares
 * the remainder equally. Works for any column count — no rigid 6-column assumption.
 */
const buildReportColWidths = (count: number): string[] => {
    if (count <= 0) return [];
    if (count === 1) return ['100%'];
    const firstWeight = 1.8;
    const restWeight = 1;
    const total = firstWeight + restWeight * (count - 1);
    return Array.from({ length: count }, (_, i) =>
        `${(((i === 0 ? firstWeight : restWeight) / total) * 100).toFixed(2)}%`,
    );
};

const DataTableChart: React.FC<DataTableChartProps> = ({
    data,
    options,
    className = '',
    title,
    badge,
}) => {
    const columns = data?.columns?.length ? data.columns : DEFAULT_COLUMNS;
    const allRows = Array.isArray(data?.rows) ? (data?.rows as Record<string, unknown>[]) : DEFAULT_ROWS;
    const variant = options?.variant ?? 'report';
    const striped = options?.striped ?? false;
    const isReport = variant === 'report';
    const maxRows = options?.maxRows;
    const rows = maxRows != null ? allRows.slice(0, maxRows) : allRows;
    const emptyMessage = options?.emptyMessage ?? 'No data found.';

    const reportCellClass = isReport
        ? `${REPORT_TABLE_TEXT_CLASS} ${REPORT_TABLE_FONT_CLASS}`.trim()
        : '';
        
    const reportHeaderCellClass = isReport
        ? `${REPORT_TABLE_HEADER_TEXT_CLASS} ${REPORT_TABLE_FONT_CLASS}`.trim()
        : '';

    return (
        <ChartFrame title={title} badge={badge} className="w-full">
        <div
            className={`w-full min-w-0 max-h-[20rem] overflow-y-auto overscroll-contain bg-white ${
                isReport
                    ? `${DATA_TABLE_CHART_REPORT_CLASS} ${REPORT_TABLE_WRAPPER_CLASS} ${REPORT_TABLE_FONT_CLASS} ${REPORT_TABLE_TEXT_CLASS} ${CHART_LIBRARY_TABLE_CLASS}`
                    : ''
            } ${className}`.trim()}
            style={isReport ? REPORT_TABLE_CELL_FONT_STYLE : undefined}
        >
            <Table
                variant={variant}
                className={isReport ? reportCellClass : ''}
                wrapperClassName="min-h-0 border-0 shadow-none rounded-none"
            >
                {isReport && (() => {
                    const colWidths = buildReportColWidths(columns.length);
                    return (
                        <colgroup>
                            {columns.map((col, i) => (
                                <col
                                    key={col.key}
                                    style={{ width: colWidths[i] ?? 'auto' }}
                                />
                            ))}
                        </colgroup>
                    );
                })()}
                <TableHead>
                    <TableHeaderRow>
                        {columns.map((col, colIndex) => (
                            <TableHeadCell
                                key={col.key}
                                align={col.align || (colIndex === 0 ? 'left' : 'right')}
                                className={reportHeaderCellClass}
                            >
                                {col.label ?? col.key.replace(/_/g, ' ')}
                            </TableHeadCell>
                        ))}
                    </TableHeaderRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableEmpty colSpan={columns.length}>{emptyMessage}</TableEmpty>
                    ) : (
                        rows.map((row, rowIndex) => (
                            <TableRow
                                key={rowIndex}
                                hover={variant !== 'report'}
                                className={
                                    striped && rowIndex % 2 === 1
                                        ? 'bg-slate-50/60'
                                        : ''
                                }
                            >
                                {columns.map((col, colIndex) => (
                                    <TableCell
                                        key={col.key}
                                        align={col.align || (colIndex === 0 ? 'left' : 'right')}
                                        className={`${resolveCellClass(col, colIndex, variant)} ${reportCellClass}`.trim()}
                                    >
                                        {col.type === 'status' ? (
                                            <Badge variant={STATUS_VARIANT_MAP[String(row[col.key])] || 'default'}>
                                                {String(row[col.key])}
                                            </Badge>
                                        ) : (
                                            formatCell(row[col.key])
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </div>
        </ChartFrame>
    );
};

export default DataTableChart;
